import { useEffect, useState } from "react";
import axios from "axios";

const NAVY = "#000000";
const ROSE = "#666666";
const CREAM = "#FFFFFF";
const PAPER = "#FFFFFF";
const HAIR = "#000000";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedPlanView({ token }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/plan/shared/${token}`);
        if (alive) setPlan(data);
      } catch (e) {
        if (alive) {
          setError(
            e?.response?.status === 404
              ? "This share link is not valid — it may have been revoked."
              : "Couldn't load this plan. Try again later."
          );
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const wrap = {
    minHeight: "100vh",
    background: CREAM,
    color: NAVY,
    fontFamily: BODY,
  };

  if (error) {
    return (
      <div
        style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        data-testid="shared-plan-error"
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <h1
            style={{
              fontFamily: DISPLAY,
              fontSize: 56,
              margin: 0,
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            Link not<br />available.
          </h1>
          <p style={{ fontSize: 15, fontWeight: 600, color: NAVY, lineHeight: 1.5 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: DISPLAY, fontSize: 20, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
          Loading…
        </div>
      </div>
    );
  }

  const open = plan.milestones.filter((m) => m.status === "open");
  const done = plan.milestones.filter((m) => m.status === "done");
  const sprint = plan.sprint || { overdue: [], this_week: [], week_start: "", week_end: "" };

  return (
    <div style={wrap} data-testid="shared-plan-view">
      {/* Header */}
      <header
        style={{
          background: PAPER,
          borderBottom: `2px solid ${HAIR}`,
          padding: "20px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ fontFamily: DISPLAY, fontSize: 22, letterSpacing: "-0.02em", textTransform: "uppercase" }}>
          Hey Sova
        </div>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 800,
            border: `2px solid ${HAIR}`,
            padding: "5px 12px",
          }}
        >
          Read-only · shared link
        </div>
      </header>

      <main style={{ maxWidth: 880, margin: "0 auto", padding: "40px 24px 80px" }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 800,
            color: NAVY,
            marginBottom: 12,
          }}
        >
          {plan.founder_name ? `${plan.founder_name} · ` : ""}Action Plan
        </div>
        <h1
          style={{
            fontFamily: DISPLAY,
            fontSize: 60,
            margin: 0,
            lineHeight: 0.92,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
          }}
        >
          {plan.board_name}
        </h1>

        {/* Progress */}
        <div
          style={{
            border: `2px solid ${HAIR}`,
            background: PAPER,
            padding: "22px 24px",
            marginTop: 28,
          }}
          data-testid="shared-progress-card"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontFamily: DISPLAY,
                fontSize: 32,
                lineHeight: 0.95,
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
              }}
            >
              {plan.progress?.percent ?? 0}% complete
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              {plan.progress?.done ?? 0} / {plan.progress?.total ?? 0} done
            </div>
          </div>
          <div
            style={{
              height: 14,
              background: "#FFFFFF",
              border: `2px solid ${HAIR}`,
            }}
          >
            <div
              style={{
                width: `${plan.progress?.percent ?? 0}%`,
                height: "100%",
                background: NAVY,
              }}
            />
          </div>
        </div>

        {/* Sprint */}
        {(sprint.overdue.length + sprint.this_week.length) > 0 && (
          <section style={{ marginTop: 32 }}>
            <h2
              style={{
                fontFamily: DISPLAY,
                fontSize: 22,
                margin: "0 0 12px",
                textTransform: "uppercase",
                letterSpacing: "-0.015em",
              }}
            >
              This sprint · {sprint.week_start} → {sprint.week_end}
            </h2>
            <div style={{ border: `2px solid ${HAIR}`, background: PAPER }}>
              {sprint.overdue.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "14px 18px",
                    borderBottom: `1px solid ${HAIR}`,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Overdue · due {m.due_date}
                  </div>
                  {m.title}
                </div>
              ))}
              {sprint.this_week.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "14px 18px",
                    borderBottom: `1px solid ${HAIR}`,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: ROSE,
                      marginBottom: 4,
                    }}
                  >
                    Due {m.due_date}
                  </div>
                  {m.title}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Open milestones */}
        <section style={{ marginTop: 36 }}>
          <h2
            style={{
              fontFamily: DISPLAY,
              fontSize: 22,
              margin: "0 0 14px",
              textTransform: "uppercase",
              letterSpacing: "-0.015em",
            }}
          >
            Open milestones ({open.length})
          </h2>
          {open.length === 0 ? (
            <p style={{ fontSize: 14, color: ROSE, fontWeight: 600 }}>No open milestones.</p>
          ) : (
            <div style={{ border: `2px solid ${HAIR}` }}>
              {open.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "14px 18px",
                    borderBottom: `1px solid ${HAIR}`,
                    fontSize: 14,
                    fontWeight: 600,
                    background: PAPER,
                  }}
                >
                  <div style={{ marginBottom: m.due_date || m.owner_role ? 4 : 0 }}>{m.title}</div>
                  {(m.due_date || m.owner_role) && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: ROSE,
                      }}
                    >
                      {m.owner_role ? `Owner: ${m.owner_role}` : ""}
                      {m.owner_role && m.due_date ? " · " : ""}
                      {m.due_date ? `Due ${m.due_date}` : ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Done */}
        {done.length > 0 && (
          <section style={{ marginTop: 36 }}>
            <h2
              style={{
                fontFamily: DISPLAY,
                fontSize: 22,
                margin: "0 0 14px",
                textTransform: "uppercase",
                letterSpacing: "-0.015em",
              }}
            >
              Done ({done.length})
            </h2>
            <div style={{ border: `2px solid ${HAIR}` }}>
              {done.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "12px 18px",
                    borderBottom: `1px solid ${HAIR}`,
                    fontSize: 13,
                    fontWeight: 600,
                    color: ROSE,
                    background: PAPER,
                    textDecoration: "line-through",
                  }}
                >
                  {m.title}
                </div>
              ))}
            </div>
          </section>
        )}

        <footer
          style={{
            marginTop: 56,
            paddingTop: 24,
            borderTop: `2px solid ${HAIR}`,
            fontSize: 11,
            color: ROSE,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span>
            Generated by Hey Sova · <a href="/" style={{ color: NAVY, textDecoration: "underline" }}>heysova.com</a>
          </span>
          <span>Last updated {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : "—"}</span>
        </footer>
      </main>
    </div>
  );
}
